import axios from "axios";
import { authHeader } from "./auth-headerService";
import { resolveApiBaseUrl } from "@/utils/hostname";

const API_URL = process.env.VUE_APP_API_URL || "http://127.0.0.1:8000/api/";

const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
    "Access-Control-Allow-Origin": "*",
  },
  timeout: 30000, // 10 seconds timeout
  withCredentials: true, // ✅ CRITICAL: Enable session support
});

// Request interceptor
apiClient.interceptors.request.use(
  (config) => {
    config.baseURL = resolveApiBaseUrl();

    const headers = authHeader();
    if (headers) {
      Object.keys(headers).forEach((key) => {
        config.headers[key] = headers[key];
      });
    }

    // Log outgoing requests in development
    if (process.env.NODE_ENV === "development") {
      console.log(
        "API Request:",
        config.method.toUpperCase(),
        config.url,
        config.data || {}
      );
    }

    // ✅ CRITICAL: Add session handling headers
    config.headers["X-Requested-With"] = "XMLHttpRequest";

    console.log(
      `📡 API Request: ${config.method.toUpperCase()} ${config.url}`,
      {
        data: config.data,
        params: config.params,
        headers: config.headers,
      }
    );

    return config;
  },
  (error) => {
    console.error("API Request Error:", error);
    return Promise.reject(error);
  }
);

// Response interceptor
apiClient.interceptors.response.use(
  (response) => {
    // Standardize successful responses
    if (process.env.NODE_ENV === "development") {
      console.log("API Response:", response.status, response.data);
    }
    return response;
  },
  (error) => {
    // Handle errors gracefully
    if (error.response) {
      // Server responded with error status
      console.error("API Error:", error.response.status, error.response.data);

      // Handle 401 Unauthorized errors
      if (error.response.status === 401) {
        // Could dispatch logout action or redirect to login
        // store.dispatch('auth/logout');
      }
    } else if (error.request) {
      // Request made but no response received
      console.error("API No Response:", error.request);
    } else {
      // Something went wrong in setting up the request
      console.error("API Config Error:", error.message);
    }

    return Promise.reject(error);
  }
);

// API Service class
export default {
  // Generic HTTP methods
  // Update the get method to handle debugging better
  async get(url, params = {}) {
    // Add debugging
    console.log(`Making GET request to: ${this.getBaseUrl()}${url}`, params);

    // Return the modified promise with better error handling
    try {
      return await apiClient.get(url, { params });
    } catch (error) {
      // Use debugUtils for more detailed logging
      if (window.debugUtils) {
        debugUtils.debugError("API Request Failed", {
          url: `${this.getBaseUrl()}${url}`,
          params,
          errorType: error.request
            ? "No Response"
            : error.response
            ? "Error Response"
            : "Request Setup Error",
          status: error.response?.status,
          message: error.message,
          responseData: error.response?.data,
        });
      }

      throw error;
    }
  },
  post(url, data = {}) {
    return apiClient.post(url, data);
  },

  put(url, data = {}) {
    return apiClient.put(url, data);
  },

  patch(url, data = {}) {
    return apiClient.patch(url, data);
  },

  delete(url) {
    return apiClient.delete(url);
  },


  getBaseUrl() {
    return resolveApiBaseUrl();
  },


};
